<template>
  <v-card color="#f0f8f6" class="overflow-hidden rounded-xl">
    <div class="text-right ma-3">
      <v-btn icon @click="$emit('cancel')">
        <v-icon large>mdi-close</v-icon>
      </v-btn>
    </div>
    <v-row justify="center" align="center" class="pb-3">
      <v-col cols="12" class="text-center text-h4">
        <div>Детали заказа {{ orderNumber }}</div>
        <div
          v-if="courseMeta && courseMeta.name"
          class="text-subtitle-1 mt-1 px-4 course-name"
        >
          {{ courseMeta.name }}
        </div>
      </v-col>
    </v-row>

    <!-- Шапка с метаданными курса — то, что раньше показывалось в отдельной
         CustomerCourseModal. Появляется только если данные были переданы извне
         (B2CView.showOrderDetails их собирает) — без них модалка работает как
         простой список сессий. -->
    <div v-if="courseMeta" class="course-info mx-4 mb-4">
      <div
        v-if="
          courseMeta.originalProductName &&
          courseMeta.originalProductName !== courseMeta.name
        "
        class="course-info__row"
      >
        <span class="course-info__label">Продукт-источник:</span>
        <span class="course-info__value">{{
          courseMeta.originalProductName
        }}</span>
      </div>
      <div v-if="courseMeta.status" class="course-info__row">
        <span class="course-info__label">Статус курса:</span>
        <span class="course-info__value">{{
          formatCourseStatus(courseMeta.status)
        }}</span>
      </div>
      <div v-if="courseMeta.stage" class="course-info__row">
        <span class="course-info__label">Этап:</span>
        <span class="course-info__value">{{
          stageLabels[courseMeta.stage] || courseMeta.stage
        }}</span>
      </div>
      <div v-if="courseMeta.patient" class="course-info__row">
        <span class="course-info__label">Пациент:</span>
        <span class="course-info__value">{{ courseMeta.patient }}</span>
      </div>
      <div v-if="courseMeta.assignedBy" class="course-info__row">
        <span class="course-info__label">Назначил:</span>
        <span class="course-info__value">{{ courseMeta.assignedBy }}</span>
      </div>
      <div v-if="courseMeta.createdAt" class="course-info__row">
        <span class="course-info__label">Дата начала курса:</span>
        <span class="course-info__value">{{
          formatShortDate(courseMeta.createdAt)
        }}</span>
      </div>
      <div v-if="courseMeta.lastWatch" class="course-info__row">
        <span class="course-info__label">Последний просмотр:</span>
        <span
          class="course-info__value"
          :class="{ 'course-info__value--danger': isLastWatchStale }"
        >
          {{ formatShortDate(courseMeta.lastWatch) }}
        </span>
      </div>
      <div v-if="courseMeta.endDate" class="course-info__row">
        <span class="course-info__label">Дата окончания:</span>
        <span class="course-info__value">{{
          formatShortDate(courseMeta.endDate)
        }}</span>
      </div>
      <div
        v-if="courseMeta.endDate && courseMeta.createdAt"
        class="course-info__row"
      >
        <span class="course-info__label">Длительность:</span>
        <span class="course-info__value">{{ courseDurationDays }} д.</span>
      </div>
    </div>

    <v-card-text>
      <div v-if="canEditStatus" class="sessions-actions">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <v-btn
                small
                outlined
                color="primary"
                :disabled="!lockedSession || timerResetInProgress"
                :loading="timerResetInProgress"
                @click="openLockedSessionNow"
              >
                <v-icon left small>mdi-timer-off-outline</v-icon>
                Открыть сессию сейчас
              </v-btn>
            </span>
          </template>
          <span v-if="lockedSession">
            Снимет таймер ожидания у сессии {{ lockedSession.id }}. Статусы и
            просмотры останутся как есть.
          </span>
          <span v-else>Ни одна сессия не ждёт таймера</span>
        </v-tooltip>
      </div>
      <v-data-table
        :headers="headers"
        :items="localSessions"
        :items-per-page="-1"
        class="elevation-0"
        hide-default-footer
      >
        <template v-slot:[`item.status`]="{ item }">
          <v-tooltip
            v-if="canEditStatus && !sessionAccessibility[item.id]"
            bottom
          >
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                <v-select
                  :value="item.status"
                  :items="statusOptions"
                  dense
                  hide-details
                  outlined
                  disabled
                  style="max-width: 160px"
                />
              </span>
            </template>
            <span>Нельзя изменить статус будущей сессии</span>
          </v-tooltip>
          <v-select
            v-else-if="canEditStatus"
            :key="`status-${item.id}-${statusSelectKey}`"
            :value="item.status"
            :items="statusOptions"
            dense
            hide-details
            outlined
            style="max-width: 160px"
            @change="onStatusChange(item, $event)"
          />
          <v-chip
            v-else
            :color="getStatusColor(item.status)"
            text-color="white"
            small
          >
            {{ formatStatus(item.status) }}
          </v-chip>
        </template>
        <template v-slot:[`item.marked_as_watched`]="{ item }">
          <v-tooltip v-if="item.marked_as_watched" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon color="orange darken-2" v-bind="attrs" v-on="on"
                >mdi-eye-check</v-icon
              >
            </template>
            <span>Клиент пропустил сессию</span>
          </v-tooltip>
          <span v-else class="text--disabled">—</span>
        </template>
        <template v-slot:[`item.playback_availability_time`]="{ item }">
          <span v-if="item.playback_availability_time">
            {{ formatDate(item.playback_availability_time) }}
          </span>
          <span v-else class="text--disabled">Доступна сразу</span>
        </template>
        <template v-slot:[`item.viewed_at`]="{ item }">
          <span v-if="item.viewed_at">
            {{ formatDate(item.viewed_at) }}
          </span>
          <span v-else class="text--disabled">Не просмотрена</span>
        </template>
        <template v-slot:[`item.view_progress`]="{ item }">
          <span v-if="item.view_progress">
            {{ formatTime(item.view_progress) }}
          </span>
          <span v-else>0:00</span>
        </template>
        <template v-slot:[`item.view_percent`]="{ item }">
          <span
            v-if="item.view_percent !== undefined && item.view_percent !== null"
          >
            {{ Math.round(item.view_percent) }}%
          </span>
          <span v-else-if="item.view_progress && item.duration">
            {{ Math.round((item.view_progress / item.duration) * 100) }}%
          </span>
          <span v-else class="text--disabled">—</span>
        </template>
      </v-data-table>
    </v-card-text>
    <v-dialog :value="rollbackTarget !== null" max-width="520" persistent>
      <v-card v-if="rollbackTarget">
        <v-card-title class="text-h6">
          Сбросить сессию {{ rollbackTarget.id }}?
        </v-card-title>
        <v-card-text>
          <p>
            Сессия {{ rollbackTarget.id }} и все последующие вернутся в статус
            «Новая»: прогресс просмотра обнулится, отметки о просмотре снимутся,
            пациенту придётся смотреть их заново.
          </p>
          <p class="mb-0">
            Если нужно только открыть следующую сессию раньше срока — отмените и
            нажмите «Открыть сессию сейчас»: просмотры останутся на месте.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cancelRollback">Отмена</v-btn>
          <v-btn color="error" depressed @click="confirmRollback">
            Сбросить сессию
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar
      v-model="snackbar"
      :timeout="3000"
      :color="snackbarColor"
      outlined
    >
      {{ snackbarText }}
    </v-snackbar>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import ApiService from "@/api/ApiService";
import { COURSE_STAGES } from "@/utils/constants";

export default {
  name: "OrderSessionsModal",
  data() {
    return {
      localSessions: [],
      rollbackTarget: null,
      statusSelectKey: 0,
      timerResetInProgress: false,
      snackbar: false,
      snackbarText: "",
      snackbarColor: "",
      stageLabels: COURSE_STAGES,
      statusOptions: [
        { text: "Новая", value: "new" },
        { text: "В процессе", value: "in_progress" },
        { text: "Завершена", value: "done" },
      ],
      headers: [
        { text: "Сессия", value: "name", sortable: true },
        { text: "Статус", value: "status", sortable: true },
        { text: "Пропущено", value: "marked_as_watched", sortable: true },
        {
          text: "Доступна с",
          value: "playback_availability_time",
          sortable: true,
        },
        { text: "Просмотрена", value: "viewed_at", sortable: true },
        { text: "Просмотрено", value: "view_progress", sortable: true },
        { text: "Процент", value: "view_percent", sortable: true },
      ],
    };
  },
  computed: {
    ...mapGetters("modals", ["modalFormComponent"]),
    ...mapGetters(["user"]),
    orderNumber() {
      return this.modalFormComponent?.orderNumber || "";
    },
    orderDataId() {
      return this.modalFormComponent?.orderDataId || null;
    },
    courseMeta() {
      // Опциональная шапка: B2CView.showOrderDetails её передаёт. Если модалка
      // открывается из другого места (без meta) — секция просто не рисуется.
      return this.modalFormComponent?.courseMeta || null;
    },
    courseDurationDays() {
      const start = this.courseMeta?.createdAt;
      const end = this.courseMeta?.endDate;
      if (!start || !end) return "—";
      const diff = new Date(end) - new Date(start);
      return Math.max(1, Math.ceil(diff / (1000 * 60 * 60 * 24)));
    },
    isLastWatchStale() {
      // Если курс «застрял» — последний просмотр более недели назад. Подсвечиваем
      // красным, чтобы менеджер заметил.
      if (
        !this.courseMeta ||
        this.courseMeta.status === "done" ||
        this.courseMeta.status === "closed"
      )
        return false;
      const MS_IN_WEEK = 7 * 24 * 60 * 60 * 1000;
      const last = this.courseMeta.lastWatch || this.courseMeta.createdAt;
      if (!last) return false;
      return Date.now() - new Date(last).getTime() > MS_IN_WEEK;
    },
    canEditStatus() {
      // Вызывающая сторона может явно запретить редактирование (карточка
      // пациента открывает модалку только на просмотр).
      if (this.modalFormComponent?.readOnlyStatus) return false;
      // Откат/смена статуса сессий доступна админу и ответственному менеджеру
      // (роль "manager"). Для остальных статус показывается как read-only chip.
      return ["admin", "manager"].includes(this.user?.role);
    },
    lockedSession() {
      const now = Date.now();
      return (
        this.localSessions.find(
          (session) =>
            session.status !== "done" &&
            session.playback_availability_time &&
            new Date(session.playback_availability_time).getTime() > now
        ) || null
      );
    },
    sessionAccessibility() {
      const result = {};
      let foundNotDone = false;
      for (const video of this.localSessions) {
        result[video.id] = !foundNotDone;
        if (video.status !== "done") foundNotDone = true;
      }
      return result;
    },
  },
  created() {
    this.localSessions = (this.modalFormComponent?.sessions || []).map((s) => ({
      ...s,
    }));
  },
  methods: {
    formatStatus(status) {
      const statusMap = {
        new: "Новая",
        in_progress: "В процессе",
        done: "Завершена",
      };
      return statusMap[status] || status;
    },
    getStatusColor(status) {
      const colorMap = {
        new: "grey",
        in_progress: "blue",
        done: "green",
      };
      return colorMap[status] || "grey";
    },
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleString("ru-RU", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    formatShortDate(value) {
      if (!value) return "—";
      return new Date(value).toLocaleDateString("ru-RU");
    },
    formatCourseStatus(status) {
      const map = {
        new: "Новый",
        in_progress: "В процессе",
        done: "Просмотрен",
        closed: "Закрыт вручную",
      };
      return map[status] || status;
    },
    formatTime(viewProgress) {
      if (viewProgress === undefined || viewProgress === null) return "0:00";
      const roundedSeconds = Math.round(viewProgress);
      const minutes = Math.floor(roundedSeconds / 60);
      const remainingSeconds = roundedSeconds % 60;
      const formattedSeconds =
        remainingSeconds < 10 ? "0" + remainingSeconds : remainingSeconds;
      return `${minutes}:${formattedSeconds}`;
    },
    async onStatusChange(item, newStatus) {
      if (newStatus === "new") {
        this.rollbackTarget = item;
        return;
      }
      await this.applyStatusChange(item, newStatus);
    },
    cancelRollback() {
      this.rollbackTarget = null;
      this.statusSelectKey += 1;
    },
    async confirmRollback() {
      const target = this.rollbackTarget;
      this.rollbackTarget = null;
      await this.applyStatusChange(target, "new");
    },
    async applyStatusChange(item, newStatus) {
      const result = await ApiService.put(
        `v1/orders/${this.orderNumber}/update_session_status`,
        { video_id: item.id, new_status: newStatus }
      );

      if (result.success === false) {
        this.statusSelectKey += 1;
        this.snackbarText =
          result.data?.error || "Ошибка обновления статуса сессии";
        this.snackbarColor = "error";
        this.snackbar = true;
        return;
      }

      await this.reloadSessions();
      this.snackbarText = "Статус сессии обновлен";
      this.snackbarColor = "success";
      this.snackbar = true;
    },
    async openLockedSessionNow() {
      this.timerResetInProgress = true;
      const result = await ApiService.post(
        `v1/orders/${this.orderNumber}/reset_session_timer`
      );
      this.timerResetInProgress = false;

      if (result.success === false) {
        this.snackbarText =
          result.data?.error || "Ошибка сброса таймера сессий";
        this.snackbarColor = "error";
        this.snackbar = true;
        return;
      }

      await this.reloadSessions();
      this.snackbarText = "Таймер снят, сессия доступна сразу";
      this.snackbarColor = "success";
      this.snackbar = true;
    },
    async reloadSessions() {
      if (!this.orderDataId) return;
      const resp = await ApiService.get(`v1/videoOrder/${this.orderDataId}`);
      if (resp.success === false) return;
      const videos = resp.data?.user_products?.[0]?.videos || [];
      this.localSessions = videos.map((v) => ({
        ...v,
        playback_availability_time: v.playback_availability_time
          ? new Date(v.playback_availability_time)
          : null,
        viewed_at: v.viewed_at ? new Date(v.viewed_at) : null,
      }));
    },
  },
};
</script>

<style scoped lang="scss">
.sessions-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
}

.course-name {
  font-weight: 500;
  color: #333;
  word-break: break-word;
}

.course-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: #ffffff;
  border-radius: 8px;
  padding: 12px 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}
.course-info__row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
}
.course-info__label {
  font-weight: 500;
  color: #666;
}
.course-info__value {
  font-weight: 400;
  color: #222;
  text-align: right;
}
.course-info__value--danger {
  color: #d32f2f;
  font-weight: 600;
}
</style>
